const N = (v: any, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
const cents = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;

type RawDesc = {
  adjustments?: any;                  // puede venir array, objeto, null o basura
  discount_amount?: number | string | null;
};
function parseDescription(raw: unknown): { amountIncl: number; labels: string[] } {
  if (!raw) return { amountIncl: 0, labels: [] };

  let parsed: RawDesc | null = null;
  try {
    parsed = typeof raw === "string" ? JSON.parse(raw) : (raw as any);
  } catch {
    return { amountIncl: 0, labels: [] };
  }

  if (!parsed || typeof parsed !== "object") {
    return { amountIncl: 0, labels: [] };
  }

  const amountIncl = N((parsed as any).discount_amount, 0);

  // ⚠️ ajustes puede venir: array | objeto | string | numero | null | undefined
  const rawAdj: any = (parsed as any).adjustments;
  let adjustments: any[] = [];

  if (Array.isArray(rawAdj)) {
    adjustments = rawAdj;
  } else if (rawAdj && typeof rawAdj === "object") {
    adjustments = [rawAdj];
  } // en cualquier otro caso queda [] (seguro)

  const labels = adjustments
    .map((a) => (a && typeof a === "object" ? String(a?.name ?? "").trim() : ""))
    .filter(Boolean);

  return { amountIncl: Math.max(0, amountIncl), labels };
}

// ————————————————————————————————————————————————————————————

export const getItemDiscountInclTax = (item: any): number => {
  const { amountIncl } = parseDescription(item?.description);
  if (amountIncl > 0) return cents(amountIncl);

  // fallback legacy
  const taxPct = N(item?.taxPercent, 0);
  const taxFactor = 1 + taxPct / 100;
  const discountAmountExcl = N(item?.discountAmount, 0); // EXCL
  const discountPercent = N(item?.discountPercent, 0);
  const priceInclTax = N(item?.priceInclTax, 0);

  const discountAmountIncl =
    discountAmountExcl > 0 ? discountAmountExcl * taxFactor : 0;

  const discountPercentAmt =
    discountPercent > 0 ? priceInclTax * (discountPercent / 100) : 0;

  return cents(discountAmountIncl + discountPercentAmt);
};

export function buildDiscountModel(
  data: any,
  opts: { kind?: "item" | "shipment" } = {},
) {
  const isShipment = opts.kind === "shipment";

  if (isShipment) {
    const base = N(data?.priceInclTax, 0); // total envío CON IVA
    return {
      base,
      final: base,
      totalDiscount: 0,
      lines: [] as Array<{ label?: string; amount?: number }>,
      isItem: false,
      isShipment: true,
    };
  }

  // ——— ITEM ———
  const qty = Math.max(1, N(data?.qtyOrdered ?? data?.qty ?? 1));

  // Base UNITARIA CON IVA
  const unitBaseIncl = cents(
    N(
      data?.priceInclTax ??
        (data?.rowTotalInclTax != null ? N(data.rowTotalInclTax) / qty : 0),
      0,
    ),
  );

  // Descuento CON IVA (description) o legacy
  const { amountIncl, labels } = parseDescription(data?.description);
  const legacyIncl = getItemDiscountInclTax(data);
  const rowDiscIncl = amountIncl > 0 ? amountIncl : legacyIncl;

  const unitDiscIncl = rowDiscIncl > 0 ? cents(rowDiscIncl / qty) : 0;
  const unitFinalIncl = Math.max(0, cents(unitBaseIncl - unitDiscIncl));

  const label = labels.length ? labels.join(" + ") : "Discount";

  return {
    base: unitBaseIncl,
    final: unitFinalIncl,
    totalDiscount: unitDiscIncl,
    lines: unitDiscIncl > 0 ? [{ label, amount: unitDiscIncl }] : [],
    isItem: true,
    isShipment: false,
  };
}

// ✔ si exportás estos helpers desde acá, mantenelos tal cual
export const getFinalInclTaxForItem = (item: any) =>
  buildDiscountModel(item, { kind: "item" }).final;

export const getFinalInclTaxForShipment = (shipment: any) =>
  buildDiscountModel(shipment, { kind: "shipment" }).final;

export const parseDiscountFromDescription = parseDescription;
export { cents }; // por si lo usás en otros puntos
